import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@/store/hooks";
import styles from "./NearbyOrders.module.scss";
import OrderDialog, {
  OrderDialogOrder,
  OrderDialogMode,
} from "@/components/OrderDialog/OrderDialog";
import {
  getAllPublishedEmergencyServices,
  getEmergencyServiceListByDistance,
  getEmergencyServiceListByDistanceLogin,
} from "@/api/emergencyServiceApi";
import Membership from "@/components/Membership";
import { useTranslation } from "react-i18next";
import OrderEmpty from '@/components/OrderEmpty/orderEmpty';
import orderNoneIcon2 from '@/assets/images/order-none2.png';

// 获取服务类别路径名（仅支持 number[]）
const getCategoryPathLabel = (categories: any[], path: number[]): string => {
  if (
    !Array.isArray(categories) ||
    categories.length === 0 ||
    !Array.isArray(path)
  )
    return "";
  let level: any[] | undefined = categories;
  const labels: string[] = [];
  for (const val of path) {
    if (!Array.isArray(level)) break;
    const node = (level as any[]).find((n: any) => n.value === val) as any;
    if (!node) break;
    labels.push(node.label);
    level = node.children;
  }
  return labels.length > 0 ? labels.join(" / ") : path.join("/");
};

export type NearbyOrder = {
  emergency_service_id: string | number;
  service_category: number[]; // 仅支持级联路径数组
  service_type: number;
  problem_description: string;
  budget: number;
  fixed_price: number;
  city_state: number;
  address: string;
  phone: string;
  appointment_time: string;
  images: any[];
  created_at: string;
  distance: number; // 添加距离字段
};

type NearbyOrdersProps = {
  orders?: NearbyOrder[];
  onExpandRange?: () => void;
  serviceCategories?: any[]; // 服务类别数据
  refresh?: number; // 添加刷新触发器
  isShowExpandRange?: boolean;
};

const NearbyOrders: React.FC<NearbyOrdersProps> = ({
  orders = [],
  onExpandRange,
  serviceCategories = [],
  refresh,
  isShowExpandRange = false,
}) => {
  //国际化
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogMode, setDialogMode] =
    React.useState<OrderDialogMode>("bidding");
  const [currentOrder, setCurrentOrder] =
    React.useState<OrderDialogOrder | null>(null);

  // 添加会员弹窗状态
  const [membershipModalOpen, setMembershipModalOpen] =
    useState<boolean>(false);

  // 获取美国州列表
  const StateList = useAppSelector((state) => state.common.StateList);

  // 获取定位
  const latitude = useAppSelector((state) => state.user.latitude);
  const longitude = useAppSelector((state) => state.user.longitude);

  // 获取用户信息
  const userInfo:any = useAppSelector((state) => state.user.userInfo);
  // 是否是会员
  const [isMember, setIsMember] = useState(false);
  const token = useAppSelector((state) => state.user.token);

  // 从接口获取订单数据
  const [apiOrders, setApiOrders] = React.useState<NearbyOrder[]>([]);
  const [loading, setLoading] = React.useState(false);

  // 进入组件时打印当前位置
  useEffect(() => {
    const locationStr = localStorage.getItem("location");
    if (locationStr) {
      try {
        console.log(
          "EmergencyService location (localStorage):",
          JSON.parse(locationStr)
        );
      } catch {
        console.log("EmergencyService location (localStorage):", locationStr);
      }
    }
    console.log("EmergencyService location (Redux):", { latitude, longitude });
  }, [latitude, longitude]);

  // 获取紧急服务订单列表
  const fetchOrders = React.useCallback(async () => {
    // 检查是否有有效的坐标
    if (!latitude || !longitude) {
      console.log("没有有效的坐标信息，使用默认坐标");
      // 如果没有坐标，可以使用默认坐标或者提示用户
    }

    setLoading(true);
    try {
      // 判断是否登录
      const userInfo = localStorage.getItem("userInfo");
      const isLogin = userInfo ? true : false;

      let res;
      if (isLogin) {
      // 使用新的获取所有已发布服务的接口
      res = await getAllPublishedEmergencyServices({
        user_latitude: 37.7749,      // 旧金山（与数据库中的服务位置匹配）
        user_longitude: -122.4194,
        page_num: 1,
        page_size: 20,
        status: 0,  // 只获取竞价中的服务
      });
      } else {
      // 使用新的获取所有已发布服务的接口
      res = await getAllPublishedEmergencyServices({
        user_latitude: 37.7749,      // 旧金山（与数据库中的服务位置匹配）
        user_longitude: -122.4194,
        page_num: 1,
        page_size: 20,
        status: 0,  // 只获取竞价中的服务
      });
      }
      console.log("API 返回数据:", res); // 调试日志
      if (res && res.code === 200) {
        const orderList = res.data?.data || res.data?.list || res.data || [];
        console.log("处理后的订单列表:", orderList,userInfo); // 调试日志
        setApiOrders(Array.isArray(orderList) ? orderList : []);
      } else {
        console.error("获取订单列表失败:", res?.msg || "未知错误");
        setApiOrders([]);
      }
    } catch (error) {
      console.error("获取订单列表失败:", error);
      setApiOrders([]);
    } finally {
      setLoading(false);
    }
  }, [latitude, longitude]);

  // 当坐标变化时重新获取数据
  useEffect(() => {
    if (latitude && longitude) {
      fetchOrders();
    }
  }, [fetchOrders, latitude, longitude]);

  // 当 refresh 属性变化时重新获取数据
  useEffect(() => {
    if (refresh) {
      fetchOrders();
    }
  }, [refresh, fetchOrders]);
  // 是否是会员
  useEffect(() => {
    setIsMember(!!userInfo?.carpool_service_member);
  }, [userInfo]);
  // 使用接口返回的数据，按创建时间排序
  const allOrders = React.useMemo(() => {
    if (!Array.isArray(apiOrders) || apiOrders.length === 0) {
      return [];
    }
    return apiOrders.sort((a, b) => {
      if (a.created_at && b.created_at) {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      }
      return 0;
    });
  }, [apiOrders]);

  const openDialog = (mode: OrderDialogMode, order: NearbyOrder) => {
    console.log('openDialog', userInfo);
    if(!userInfo.is_engineer){
      navigate(`/yourtownloop/engineer_zone`)
      return
    }
    setDialogMode(mode);
    setCurrentOrder({
      imageUrl:
        order.images && order.images.length > 0
          ? order.images[0].product_media_file_url
          : "/api/placeholder/640/420?text=No+Image",
      title: order.problem_description,
      distanceMiles: `${
        order.distance ? order.distance.toFixed(2) : "0.00"
      } Miles From You`,
      appointment_time: order.appointment_time || "",
      address: order.address,
      mechanism: mode === "bidding" ? "Bidding" : "Fixed Price",
      problemDescription: order.problem_description,
      price: mode === "fixed" ? order.fixed_price : order.budget,
      emergency_service_id: String(order.emergency_service_id),
    });
    setDialogOpen(true);
  };
  const getDescription = (order:any) => {
    console.log("urgency_level:", order.urgency_level);
    switch(order.urgency_level) {
      case 0:
        return t('membership.Critical');
      case 1:
        return t('membership.Urgent');
      case 2:
        return t('membership.Rush');
      case 3:
        return t('membership.Normal');
    }
  };
  const getUrgencyClass = (order:any) => {
  switch(order.urgency_level) {
    case 0:
      return styles.Critical;
    case 1:
      return styles.Urgent;
    case 2:
      return styles.Rush;
    case 3:
      return styles.Normal;
   
  }
};
  const handleConfirm = () => {
    setDialogOpen(false);
    // 确认后刷新订单列表
    fetchOrders();
  };

  // 处理会员购买弹窗的回调函数
  const handleMembershipCancel = () => {
    setMembershipModalOpen(false);
  };

  const handleMembershipConfirm = (values: any) => {
    console.log("Membership purchase confirmed:", values);

    // 检查是否是支付成功
    if (values.paymentSuccess) {
      setMembershipModalOpen(false);
      // 支付成功后可以刷新订单列表或其他操作
      fetchOrders();
    } else {
      setMembershipModalOpen(false);
    }
  };

  // 处理展开搜索范围的点击事件
  const handleExpandRangeClick = () => {
    setMembershipModalOpen(true);
  };

  // 获取用户VIP信息中的最大英里数（登录后比较 vipTempMiles 与 userInfo 最大 mile，取更大；未登录返回10）
  const getMaxMilesFromVipInfo = () => {
    // 未登录：一律显示 10
    if (!token) return 10;

    // 读取临时英里数
    let tempMiles = 0;
    try {
      const tempStr = localStorage.getItem("vipTempMiles");
      const n = tempStr ? Number(tempStr) : NaN;
      if (Number.isFinite(n) && n > 0) tempMiles = n;
    } catch {}

    // 计算 userInfo 中的最大英里数（现在检查拼车会员 vip_type=1）
    let vipMilesMax = 0;
    if (userInfo && Array.isArray(userInfo.vip_info)) {
      const carpoolVips = (userInfo.vip_info as any[]).filter(
        (vip: any) => vip?.vip_type === 1 && !vip?.is_delete
      );
      const allMiles: number[] = [];
      carpoolVips.forEach((vip: any) => {
        const ext = vip?.extension;
        if (Array.isArray(ext)) {
          ext.forEach((e: any) => {
            const m = e?.mile;
            if (typeof m === "number" && Number.isFinite(m)) allMiles.push(m);
          });
        }
      });
      if (allMiles.length > 0) vipMilesMax = Math.max(...allMiles);
    }

    const result = Math.max(tempMiles || 0, vipMilesMax || 0);
    return result > 0 ? result : 10;
  };

  // 获取显示的英里数
  const displayMiles = getMaxMilesFromVipInfo();

  return (
    <section
      className={styles.nearbyOrdersSection}
      aria-labelledby="nearbyOrdersHeading"
    >
      <div className={styles.headerRow}>
        <div className={styles.titleGroup}>
          <h2 id="nearbyOrdersHeading" className={styles.title}>
            {t('membership.EmergencyService')}
          </h2>
          {/* 这里是增加搜索范围的会员增值服务 */}
        </div>
        {(isMember && isShowExpandRange) && (
          <span
            className={`${styles['cursor-pointer']}`}
            onClick={() => navigate("/emergencyService")}
          >
            {t("membership.ViewAll")}
          </span>
        )}
        {/* 这里是增加搜索范围的会员增值服务 */}
        {/* <button className={styles.expandLink} onClick={handleExpandRangeClick} type="button">
                    {t('membership.ClickRange')}
                </button> */}
      </div>
      <div className="mb-[20px] text-sm text-gray-500">
        {isMember ? t('membership.EmergencyServiceMark') : t('membership.EmergencyServiceNoneMark')}
      </div>
      {isMember ? <div className={styles.grid}>
        {allOrders.map((order) => (
          <article key={order.emergency_service_id} className={styles.card}>
            <div className={styles.imageWrap}>
              <span className={`${styles.urgency_level} ${getUrgencyClass(order)}`}>{getDescription(order)}</span>
              <img
                onClick={() =>
                  navigate(
                    `/EmergencyServiceDetail/${order.emergency_service_id}`
                  )
                }
                src={
                  order.images && order.images.length > 0
                    ? order.images[0].product_media_file_url
                    : ""
                }
                alt={order.problem_description}
                style={{ width: "100%", height: "200px", objectFit: "contain" }}
              />
              <span className={styles.badge}>
                {order.distance ? order.distance.toFixed(2) : ""}{" "}
                {t("searchList.MilesFromYou")}
              </span>
            </div>
            <h3
              className={styles.cardTitle}
              onClick={() =>
                navigate(`/order-detail/${order.emergency_service_id}`)
              }
              style={{ cursor: "pointer" }}
            >
              {getCategoryPathLabel(
                serviceCategories as any[],
                order.service_category
              )}
            </h3>

            <ul className={styles.metaList}>
              <li className={styles.metaItem}>
                {/* 隐藏部分地址 */}
                {order.address
                  ? order.address.slice(0, 10) + "****"
                  : `${t("searchList.Address")} N/A`}
                {StateList &&
                  StateList.length > 0 &&
                  (() => {
                    const stateItem = StateList.find(
                      (item: any) =>
                        item.value === order.city_state ||
                        StateList.indexOf(item) === order.city_state - 1
                    );
                    return stateItem ? `, ${stateItem.label}` : "";
                  })()}
              </li>
              <li className={styles.metaItem}>
                {t("searchList.AppointmentTime")}:{" "}
                {order.appointment_time || "N/A"}
              </li>
              <li className={styles.metaItem}>
                {t("searchList.ProblemDescription")}:{" "}
                {order.problem_description || "N/A"}
              </li>
              <li className={styles.priceRow}>
                <span className={styles.priceLabel}>
                  {t("searchList.Price")} :
                </span>
                <span className={styles.priceValue}>
                  ${order.fixed_price || order.budget || "N/A"}
                </span>
              </li>
            </ul>

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.bidBtn}
                onClick={() => openDialog("bidding", order)}
              >
                {t("searchList.Bidding")}
              </button>
              {/* <button
                type="button"
                className={styles.fixedBtn}
                onClick={() => openDialog("fixed", order)}
              >
                {t("searchList.FixedPrice")}
              </button> */}
            </div>
          </article>
        ))}
      </div> : <OrderEmpty img={orderNoneIcon2} text={t('membership.NoOrdersAvailable')}></OrderEmpty>}
      {currentOrder && (
        <OrderDialog
          open={dialogOpen}
          mode={dialogMode}
          order={currentOrder}
          onCancel={() => setDialogOpen(false)}
          onConfirm={() => handleConfirm()}
        />
      )}

      {/* 会员购买弹窗 */}
      <Membership
        open={membershipModalOpen}
        onCancel={handleMembershipCancel}
        onConfirm={handleMembershipConfirm}
        membershipType={0}
      />
    </section>
  );
};

export default NearbyOrders;
